import * as React from "react"
import * as PopoverPrimitive from "@radix-ui/react-popover"

import { cn } from "@/lib/utils"

function Popover({
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  defaultOpen,
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Root>) {
  const isControlled = controlledOpen !== undefined;
  const [internalOpen, setInternalOpen] = React.useState(defaultOpen ?? false);
  const lastChangeTimeRef = React.useRef<number>(0);
  const DEBOUNCE_MS = 100;

  const handleOpenChange = React.useCallback((newOpen: boolean) => {
    const now = Date.now();
    const timeSinceLastChange = now - lastChangeTimeRef.current;

    if (timeSinceLastChange < DEBOUNCE_MS) {
      return;
    }

    lastChangeTimeRef.current = now;

    if (isControlled) {
      controlledOnOpenChange?.(newOpen);
    } else {
      setInternalOpen(newOpen);
      controlledOnOpenChange?.(newOpen);
    }
  }, [isControlled, controlledOnOpenChange]);

  const open = isControlled ? controlledOpen : internalOpen;

  return (
    <PopoverPrimitive.Root
      data-slot="popover"
      open={open}
      onOpenChange={handleOpenChange}
      {...props}
    />
  );
}

function PopoverTrigger({
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Trigger>) {
  return <PopoverPrimitive.Trigger data-slot="popover-trigger" {...props} />
}

function PopoverContent({
  className,
  align = "center",
  sideOffset = 4,
  container,
  variant = "theme",
  zIndex,
  style,
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Content> & { container?: HTMLElement | null, reset?: boolean | null, variant?: "default" | "theme", zIndex?: number }) {
  return (
    <PopoverPrimitive.Portal container={container}>
      <PopoverPrimitive.Content
        data-slot="popover-content"
        align={align}
        sideOffset={sideOffset}
        className={cn(
          variant === "default" && "bg-white shadow-md border border-gray-200 rounded-lg",
          variant === "theme" && "bg-theme-0 text-theme-0 font-theme border border-theme rounded-lg shadow-theme w-max",
          "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-[100] origin-(--radix-popover-content-transform-origin) outline-hidden",
          className
        )}
        style={zIndex !== undefined ? { zIndex, ...style } : style}
        {...props}
      />
    </PopoverPrimitive.Portal>
  )
}

function PopoverAnchor({
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Anchor>) {
  return <PopoverPrimitive.Anchor data-slot="popover-anchor" {...props} />
}

export { Popover, PopoverTrigger, PopoverContent, PopoverAnchor }
